import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiError, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, FloppyDisk, Users, Cube, Image as ImageIcon, IdentificationCard,
  Buildings, ListChecks, Clock, Plus, PencilSimple, Trash, Power, Lock,
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super-Admin" },
  { value: "admin", label: "Administrator" },
  { value: "management", label: "Geschäftsführung" },
  { value: "manager", label: "Verwalter:in" },
  { value: "accountant", label: "Buchhaltung" },
  { value: "support", label: "Support" },
  { value: "viewer", label: "Nur lesen" },
  { value: "tenant", label: "Mieter (Portal)" },
  { value: "owner", label: "Eigentümer (Portal)" },
];

export default function MandantenDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/tenants/${id}`);
      setTenant(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  if (!tenant) return <div className="p-8 text-center text-slate-500">Lade Mandant…</div>;

  return (
    <div className="space-y-5 max-w-6xl" data-testid="mandanten-detail">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" className="h-7 px-2 -ml-2 gap-1 text-slate-600" onClick={() => navigate("/mandanten")}>
          <ArrowLeft size={14} /> Zurück zur Übersicht
        </Button>
        <div className="flex items-start justify-between flex-wrap gap-3 mt-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 font-mono">
              {tenant.id?.slice(0, 8)}
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{tenant.display_name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {tenant.legal_name || tenant.name}
              {tenant.customer_number ? <> · Kundennr. <span className="font-mono">{tenant.customer_number}</span></> : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${tenant.status === "active" ? "bg-emerald-100 text-emerald-800" : tenant.status === "trial" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
              {tenant.status === "active" ? "Aktiv" : tenant.status === "trial" ? "Testphase" : "Gesperrt"}
            </span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="mt-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="settings" data-testid="mt-tab-settings">Einstellungen</TabsTrigger>
          <TabsTrigger value="users" data-testid="mt-tab-users">Nutzer</TabsTrigger>
          <TabsTrigger value="modules" data-testid="mt-tab-modules">Module</TabsTrigger>
          <TabsTrigger value="branding" data-testid="mt-tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="audit" data-testid="mt-tab-audit">Audit-Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab tenant={tenant} /></TabsContent>
        <TabsContent value="settings"><SettingsTab tenant={tenant} onChange={load} /></TabsContent>
        <TabsContent value="users"><UsersTab tenant={tenant} /></TabsContent>
        <TabsContent value="modules"><ModulesTab tenant={tenant} onChange={load} /></TabsContent>
        <TabsContent value="branding"><BrandingTab tenant={tenant} onChange={load} /></TabsContent>
        <TabsContent value="audit"><AuditTab tenant={tenant} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ====================== Übersicht =====================

function OverviewTab({ tenant }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mt-overview">
      <Kpi icon={Buildings} label="Objekte" value={tenant.properties_count || 0} k="properties" />
      <Kpi icon={Cube} label="Einheiten" value={tenant.units_count || 0} k="units" />
      <Kpi icon={ListChecks} label="Aktive Mietverhältnisse" value={tenant.active_tenancies || 0} k="tenancies" />
      <Kpi icon={Users} label="Nutzer" value={tenant.users_count || 0} k="users" />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, k }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`mt-kpi-${k}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        <Icon size={12} /> {label}
      </div>
      <div className="text-3xl font-semibold tabular-nums mt-1.5 text-slate-900">{value}</div>
    </div>
  );
}

// ====================== Einstellungen =====================

function SettingsTab({ tenant, onChange }) {
  const [form, setForm] = useState(tenant);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = { ...form };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    delete payload.users_count; delete payload.properties_count;
    delete payload.units_count; delete payload.active_tenancies; delete payload.contacts_count;
    delete payload.is_platform; delete payload.modules; delete payload.module_settings;
    delete payload.logo_storage_path;
    try {
      await api.patch(`/tenants/${tenant.id}`, payload);
      toast.success("Stammdaten gespeichert");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-4" data-testid="mt-settings">
      <div className="text-sm font-semibold">Stammdaten</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Anzeigename"><Input value={form.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="mt-display-name" /></F>
        <F label="Rechtlicher Name"><Input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} /></F>
        <F label="Kundennummer"><Input value={form.customer_number || ""} onChange={(e) => set("customer_number", e.target.value)} data-testid="mt-customer-number" /></F>
        <F label="Status">
          <Select value={form.status || "active"} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="mt-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="trial">Testphase</SelectItem>
              <SelectItem value="suspended">Gesperrt</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <F label="Plan"><Input value={form.plan || ""} onChange={(e) => set("plan", e.target.value)} /></F>
        <F label="E-Mail"><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></F>
        <F label="Telefon"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></F>
        <F label="Website"><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} /></F>
      </div>

      <div className="text-sm font-semibold pt-2">Anschrift</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Straße"><Input value={form.address_street || ""} onChange={(e) => set("address_street", e.target.value)} /></F>
        <F label="PLZ"><Input value={form.address_zip || ""} onChange={(e) => set("address_zip", e.target.value)} /></F>
        <F label="Stadt"><Input value={form.address_city || ""} onChange={(e) => set("address_city", e.target.value)} /></F>
      </div>

      <div className="text-sm font-semibold pt-2">Steuer & Register</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Steuernummer"><Input value={form.tax_id || ""} onChange={(e) => set("tax_id", e.target.value)} /></F>
        <F label="Registergericht"><Input value={form.register_court || ""} onChange={(e) => set("register_court", e.target.value)} /></F>
        <F label="Registernummer"><Input value={form.register_number || ""} onChange={(e) => set("register_number", e.target.value)} /></F>
      </div>

      <div className="text-sm font-semibold pt-2">Bank</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Bankname"><Input value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} /></F>
        <F label="IBAN"><Input value={form.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} /></F>
        <F label="BIC"><Input value={form.bank_bic || ""} onChange={(e) => set("bank_bic", e.target.value)} /></F>
      </div>

      <div className="text-sm font-semibold pt-2">Interne Notizen</div>
      <textarea
        className="w-full border border-slate-200 rounded-md p-2 text-sm font-mono"
        rows={3}
        value={form.notes || ""}
        onChange={(e) => set("notes", e.target.value)}
      />

      <div className="flex justify-end">
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="mt-save-settings">
          <FloppyDisk size={16} /> Speichern
        </Button>
      </div>
    </div>
  );
}

// ====================== Nutzer =====================

function UsersTab({ tenant }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/tenants/${tenant.id}/users`);
    setUsers(data || []);
  };
  useEffect(() => { load(); }, [tenant.id]);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/tenants/${tenant.id}/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Nutzer deaktiviert" : "Nutzer aktiviert");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="mt-users">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{users.length} Nutzer in diesem Mandant</div>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => { setEditing(null); setOpen(true); }} data-testid="mt-add-user-btn">
          <Plus size={14} /> Neuer Nutzer
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Name / E-Mail</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Rolle</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`mt-user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3 text-sm">{ROLE_OPTIONS.find((r) => r.value === u.role)?.label || u.role}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                    {u.is_active ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`mt-edit-user-${u.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleActive(u)} data-testid={`mt-toggle-user-${u.id}`} title={u.is_active ? "Deaktivieren" : "Aktivieren"}><Power size={14} /></Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">Keine Nutzer</td></tr>}
          </tbody>
        </table>
      </div>
      <UserDialog open={open} onOpenChange={setOpen} tenantId={tenant.id} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function UserDialog({ open, onOpenChange, tenantId, editing, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (editing) setForm({ ...editing });
    else setForm({ email: "", password: "", name: "", first_name: "", last_name: "", role: "manager" });
  }, [editing, open]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/tenants/${tenantId}/users/${editing.id}`, {
          name: form.name,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          phone: form.phone,
          position: form.position,
          is_active: form.is_active,
        });
        toast.success("Nutzer aktualisiert");
      } else {
        await api.post(`/tenants/${tenantId}/users`, {
          email: form.email, password: form.password,
          name: form.name || `${form.first_name || ""} ${form.last_name || ""}`.trim() || form.email,
          role: form.role || "manager",
        });
        toast.success("Nutzer angelegt");
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mt-user-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Nutzer bearbeiten" : "Neuer Nutzer"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!editing && (
            <>
              <F label="E-Mail (Login)"><Input type="email" required value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="mt-user-email" /></F>
              <F label="Initial-Passwort"><Input type="password" minLength={6} required value={form.password || ""} onChange={(e) => set("password", e.target.value)} data-testid="mt-user-password" /></F>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label="Vorname"><Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} /></F>
            <F label="Nachname"><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} /></F>
          </div>
          <F label="Rolle">
            <Select value={form.role || "manager"} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="mt-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          {editing && (
            <F label="Telefon"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></F>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="mt-user-save">{editing ? "Speichern" : "Anlegen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====================== Module =====================

function ModulesTab({ tenant, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState(new Set(tenant.modules || []));

  useEffect(() => {
    api.get("/tenants/modules/catalog").then((r) => setCatalog(r.data || []));
  }, []);
  useEffect(() => { setActive(new Set(tenant.modules || [])); }, [tenant.modules]);

  const toggle = (key, locked) => {
    if (locked) {
      toast.info("Kernmodul – nicht deaktivierbar");
      return;
    }
    const next = new Set(active);
    if (next.has(key)) next.delete(key); else next.add(key);
    setActive(next);
  };

  const save = async () => {
    try {
      await api.put(`/tenants/${tenant.id}/modules`, { modules: Array.from(active) });
      toast.success("Module aktualisiert");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const groups = useMemo(() => {
    const m = {};
    for (const mod of catalog) (m[mod.group] = m[mod.group] || []).push(mod);
    return m;
  }, [catalog]);

  return (
    <div className="space-y-4" data-testid="mt-modules">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">Aktive Module</div>
          <div className="text-xs text-slate-500">Nicht gebuchte Module werden im Frontend ausgeblendet.</div>
        </div>
        <div className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{active.size}</span> / {catalog.length} aktiv
        </div>
      </div>
      <div className="space-y-5">
        {Object.entries(groups).map(([group, mods]) => (
          <div key={group}>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mods.map((m) => {
                const isActive = active.has(m.key);
                return (
                  <div
                    key={m.key}
                    className={`border rounded-xl p-4 transition-all ${isActive ? "border-slate-300 bg-white shadow-sm" : "border-slate-200 bg-slate-50/60"}`}
                    data-testid={`module-card-${m.key}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {m.label}
                          {m.locked && <Lock size={12} className="text-slate-400" title="Kernmodul" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{m.description}</div>
                      </div>
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => toggle(m.key, m.locked)}
                        disabled={m.locked}
                        data-testid={`module-toggle-${m.key}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end pt-2 sticky bottom-0 bg-gradient-to-t from-slate-50 to-transparent pt-6">
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="mt-save-modules">
          <FloppyDisk size={16} /> Modul-Konfiguration speichern
        </Button>
      </div>
    </div>
  );
}

// ====================== Branding =====================

function BrandingTab({ tenant, onChange }) {
  const [form, setForm] = useState({ primary_color: tenant.primary_color || "#0F172A", accent_color: tenant.accent_color || "#2563EB" });
  const [buster, setBuster] = useState(0);
  const fileRef = useRef(null);

  const save = async () => {
    try {
      await api.patch(`/tenants/${tenant.id}`, { ...tenant, ...form });
      toast.success("Branding gespeichert");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/tenants/${tenant.id}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error(`Upload fehlgeschlagen: ${r.status}`);
      toast.success("Logo aktualisiert");
      setBuster(Date.now());
      e.target.value = "";
      onChange?.();
    } catch (err) { toast.error(err.message); }
  };

  const logoSrc = tenant.logo_url
    ? `${tenant.logo_url.startsWith("http") ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${tenant.logo_url}`}${tenant.logo_url.includes("?") ? "&" : "?"}t=${buster}`
    : null;

  return (
    <div className="space-y-4" data-testid="mt-branding">
      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ImageIcon size={16} /> Logo</div>
        <div className="flex items-center gap-4">
          <div className="w-40 h-20 border border-slate-200 rounded-md bg-slate-50 flex items-center justify-center overflow-hidden">
            {logoSrc ? <img src={logoSrc} alt="Logo" className="max-h-full max-w-full object-contain p-2" /> : <Buildings size={28} className="text-slate-400" />}
          </div>
          <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={uploadLogo} data-testid="mt-logo-input" />
          <Button onClick={() => fileRef.current?.click()} className="bg-slate-900 hover:bg-slate-800" data-testid="mt-upload-logo-btn">Logo hochladen</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
        <div className="text-sm font-semibold">Farben</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Primärfarbe"><Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 p-1" data-testid="mt-color-primary" /></F>
          <F label="Akzentfarbe"><Input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 p-1" data-testid="mt-color-accent" /></F>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 gap-2"><FloppyDisk size={16} /> Speichern</Button>
        </div>
      </div>
    </div>
  );
}

// ====================== Audit-Log =====================

function AuditTab({ tenant }) {
  // Nutzt audit_log gefiltert auf entity_type=tenant + entity_id=tenant.id
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    // Hilfsendpoint existiert noch nicht generisch – nutze Settings-History falls vorhanden
    // → fallback: leere Liste mit Hinweis
    api.get(`/settings/company/history`).then((r) => setEntries(r.data || [])).catch(() => setEntries([]));
  }, [tenant.id]);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid="mt-audit">
      <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Clock size={16} /> Letzte Aktivitäten</div>
      {entries.length === 0 ? (
        <div className="text-sm text-slate-500 italic">Noch keine Audit-Einträge.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {entries.slice(0, 50).map((e, i) => (
            <li key={i} className="py-2 text-sm flex items-center justify-between gap-3">
              <div>
                <span className="font-medium">{e.user_name || "—"}</span>
                <span className="text-slate-500 text-xs ml-2">{e.action}</span>
              </div>
              <div className="text-[11px] text-slate-500 font-mono">{new Date(e.created_at).toLocaleString("de-DE")}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ====================== Helpers =====================

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-600 mb-1">{label}</Label>
      {children}
    </div>
  );
}
